import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ConfirmDialog from '../../../shared/ConfirmDialog';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import CommercialTermsPrompt, { hasCommercialTerms } from './CommercialTermsPrompt';
import '../../../shared/StyledInput.css';

const FIELD_LABEL_STYLE = { fontSize: 13, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

// Reopen a closed Prospect (issue #246) — shared by the Relationship card
// and the Pipeline's Revisit column. Asks which open Stage to return to,
// defaulting to the one it was closed from; a Stage needing commercial
// terms the record doesn't have yet gets the Tier prompt next.
// onReopened(entity) receives the updated record.
function ReopenProspectDialog({ entity, onCancel, onReopened }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const prospect = (entity.relationships || []).find((r) => r.relationship_type?.system_key === 'prospect');
  const [openStages, setOpenStages] = useState([]);
  const [stageId, setStageId] = useState(prospect?.closed_from_stage_id ? String(prospect.closed_from_stage_id) : '');
  const [needsTerms, setNeedsTerms] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get('/api/relationship-types', { params: { status: 'active' } })
      .then((res) => {
        const prospectType = (res.data.data || []).find((t) => t.system_key === 'prospect');
        const stages = (prospectType?.stages || []).filter((s) => s.is_open && s.is_active);
        setOpenStages(stages);
        setStageId((cur) => (stages.some((s) => String(s.id) === cur) ? cur : String(stages[0]?.id || '')));
      })
      .catch((err) => showError(err, 'Could not load open Stages'));
    // eslint-disable-next-line
  }, []);

  const stage = openStages.find((s) => String(s.id) === stageId);

  const reopen = async (terms = {}) => {
    try {
      const res = await axios.post(`/api/commercial-entities/${entity.id}/reopen-prospect`, { stage_id: Number(stageId), ...terms });
      dismissError();
      onReopened(res.data.data);
    } catch (err) {
      showError(err, 'Reopen failed');
      throw err;
    }
  };

  const confirm = async () => {
    if (stage?.requires_commercial_terms && !hasCommercialTerms(entity)) {
      setNeedsTerms(true);
      return;
    }
    setSaving(true);
    try {
      await reopen();
    } catch (err) {
      setSaving(false);
    }
  };

  if (needsTerms) {
    return (
      <CommercialTermsPrompt
        entity={entity}
        stageName={stage.name}
        title={`Reopen ${entity.name} at ${stage.name}`}
        confirmLabel="Reopen"
        savingLabel="Reopening..."
        onCancel={() => setNeedsTerms(false)}
        onConfirm={reopen}
      />
    );
  }

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => { if (!next && !saving) onCancel(); }}
      title={`Reopen ${entity.name}`}
      description="It goes back on the Pipeline at the Stage picked."
      footer={(
        <div style={{ display: 'flex', gap: 8 }}>
          <CancelButton onClick={onCancel} disabled={saving} />
          <SaveButton type="button" onClick={confirm} loading={saving} disabled={!stageId}>
            {saving ? 'Reopening...' : 'Reopen'}
          </SaveButton>
        </div>
      )}
    >
      <div>
        <label style={FIELD_LABEL_STYLE}>Return to</label><br />
        <select className="styled-input" value={stageId} onChange={(e) => setStageId(e.target.value)} style={FIELD_INPUT_STYLE}>
          {openStages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
    </ConfirmDialog>
  );
}

export default ReopenProspectDialog;
